import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { BackendTables, ContactV2, PersonV2 } from "interfaces/db";
import { FilterOperator, SelectColumn } from "interfaces/sessions";
import { PROSPECT_INFO_COLUMNS, SESSION_METRICS_COLUMNS, SessionV2Columns } from "interfaces/table";
import { getServicesManager } from "services";

export interface NamedPersonSearchState {
  items: PersonV2[];
  isLoading: boolean;
  error: string | null;
  lastQuery: string | null;
}

export type NumberSearchItem = {
  person_name: string | null;
  prospect_phone_value: string;
}

export interface NumberSearchState {
  items: NumberSearchItem[];
  isLoading: boolean;
  error: string | null;
  lastQuery: string | null;
}

export interface ContactSearchState {
  items: ContactV2[];
  isLoading: boolean;
  error: string | null;
  lastQuery: string | null;
}

interface SearchState {
  searches: { [key: string]: NamedPersonSearchState };
  phoneNumberSearches: { [key: string]: NumberSearchState };
  contactSearches: { [key: string]: ContactSearchState };
}

const initialState: SearchState = {
  searches: {},
  phoneNumberSearches: {},
  contactSearches: {}
};

export const namedPersonSearchSlice = createSlice({
  name: 'namedPersonSearch',
  initialState,
  reducers: {
    startLoading: (state, action: PayloadAction<{ query: string }>) => {
      const { query } = action.payload;
      if (!state.searches[query]) {
        state.searches[query] = {
          items: [],
          isLoading: true,
          error: null,
          lastQuery: query
        };
      } else {
        state.searches[query].isLoading = true;
        state.searches[query].error = null;
      }
    },
    startPhoneLoading: (state, action: PayloadAction<{ query: string }>) => {
      const { query } = action.payload;
      if (!state.phoneNumberSearches[query]) {
        state.phoneNumberSearches[query] = {
          items: [],
          isLoading: true,
          error: null,
          lastQuery: query
        };
      } else {
        state.phoneNumberSearches[query].isLoading = true;
        state.phoneNumberSearches[query].error = null;
      }
    },
    searchResultsLoaded: (state, action: PayloadAction<{
      query: string;
      items: PersonV2[];
    }>) => {
      const { query, items } = action.payload;
      state.searches[query] = {
        items,
        isLoading: false,
        error: null,
        lastQuery: query
      };
    },
    phoneSearchResultsLoaded: (state, action: PayloadAction<{
      query: string;
      items: NumberSearchItem[];
    }>) => {
      const { query, items } = action.payload;
      // remove duplicates
      const getKey = (item: NumberSearchItem) => `${item.prospect_phone_value}-${item.person_name}`;
      const usedItems = new Set<string>();
      const filteredItems: NumberSearchItem[] = []
      for (const item of items) {
        const key = getKey(item);
        if (usedItems.has(key)) {
          continue;
        }
        usedItems.add(key);
        filteredItems.push(item);
      }
      state.phoneNumberSearches[query] = {
        items: filteredItems,
        isLoading: false,
        error: null,
        lastQuery: query
      };
    },
    searchFailed: (state, action: PayloadAction<{ query: string; error: string }>) => {
      const { query, error } = action.payload;
      if (!state.searches[query]) {
        state.searches[query] = {
          items: [],
          isLoading: false,
          error,
          lastQuery: query
        };
      } else {
        state.searches[query].isLoading = false;
        state.searches[query].error = error;
      }
    },
    phoneSearchFailed: (state, action: PayloadAction<{ query: string; error: string }>) => {
      const { query, error } = action.payload;
      if (!state.phoneNumberSearches[query]) {
        state.phoneNumberSearches[query] = {
          items: [],
          isLoading: false,
          error,
          lastQuery: query
        };
      } else {
        state.phoneNumberSearches[query].isLoading = false;
        state.phoneNumberSearches[query].error = error;
      }
    },
    clearSearch: (state, action: PayloadAction<{ query: string }>) => {
      const { query } = action.payload;
      delete state.searches[query];
    },
    clearPhoneSearch: (state, action: PayloadAction<{ query: string }>) => {
      const { query } = action.payload;
      delete state.phoneNumberSearches[query];
    },
    clearAllSearches: (state) => {
      state.searches = {};
      state.phoneNumberSearches = {};
      state.contactSearches = {};
    },
    searchContact: (state, action: PayloadAction<{ query: string }>) => {
      const { query } = action.payload;
      if (!state.contactSearches[query]) {
        state.contactSearches[query] = {
          items: [],
          isLoading: true,
          error: null,
          lastQuery: query
        };
      } else {
        state.contactSearches[query].isLoading = true;
        state.contactSearches[query].error = null;
      }
    },
    contactSearchResultsLoaded: (state, action: PayloadAction<{
      query: string;
      items: ContactV2[];
    }>) => {
      const { query, items } = action.payload;
      state.contactSearches[query] = {
        items,
        isLoading: false,
        error: null,
        lastQuery: query
      };
    },
    contactSearchFailed: (state, action: PayloadAction<{ query: string; error: string }>) => {
      const { query, error } = action.payload;
      if (!state.contactSearches[query]) {
        state.contactSearches[query] = {
          items: [],
          isLoading: false,
          error,
          lastQuery: query
        };
      } else {
        state.contactSearches[query].isLoading = false;
        state.contactSearches[query].error = error;
      }
    }
  }
});

export const {
  startLoading,
  startPhoneLoading,
  searchResultsLoaded,
  phoneSearchResultsLoaded,
  searchFailed,
  phoneSearchFailed,
  clearSearch,
  clearPhoneSearch,
  clearAllSearches,
  searchContact,
  contactSearchResultsLoaded,
  contactSearchFailed
} = namedPersonSearchSlice.actions;

// Thunk action to perform the search
export function searchNamedPerson(query: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(startLoading({ query }));
      
      const results = await getServicesManager().getProspectsViaName(query);
      if (results) {
        dispatch(searchResultsLoaded({
          query,
          items: results
        }));
      } else {
        dispatch(searchFailed({
          query,
          error: "No results found"
        }));
      }
    } catch (error) {
      dispatch(searchFailed({
        query,
        error: error instanceof Error ? error.message : "An error occurred during search"
      }));
    }
  };
}

function normalizePhoneNumber(phoneNumber: string) {
  // remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  // remove leading 1 if present
  if (cleaned.length < 10) return null;
  if (cleaned.length === 10) return `1${cleaned}`;
  return cleaned;
}

export function searchPhoneNumber(query: string) {
  return async (dispatch: Dispatch) => {
    try {
      const normalized = normalizePhoneNumber(query);
      if (!normalized) {
        dispatch(phoneSearchFailed({
          query,
          error: "Invalid phone number format"
        }));
        return;
      }

      dispatch(startPhoneLoading({ query }));

      const selects: SelectColumn[] = [{
        column_name: SessionV2Columns.SESSION_ID,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.STARTED_AT,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.AGENT_ADDRESS,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.CUSTOMER_ADDRESS,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.RESOURCE_ID,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.CONVERSATION_TYPE,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SESSION_METRICS_COLUMNS.PLATFORM,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: PROSPECT_INFO_COLUMNS.PROSPECT_NAME,
        table: BackendTables.PROSPECT_INFO,
      }];

      const results = await getServicesManager().getListUserSessionsV5({
        cnf: [[{
          table: BackendTables.SESSIONS_V2,
          column_name: SessionV2Columns.CUSTOMER_ADDRESS,
          operator: FilterOperator.EQ,
          value_safe: normalized
        }]],
        select: selects
      });

      if (results && results.length > 0) {
        // Transform session results into NumberSearchItem format
        const transformedResults: NumberSearchItem[] = results.map(session => ({
          person_name: session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_NAME) || 'Unknown',
          prospect_phone_value: session.customer_address || normalized
        }))
        dispatch(phoneSearchResultsLoaded({
          query,
          items: transformedResults
        }));
      } else {
        dispatch(phoneSearchFailed({
          query,
          error: "No results found"
        }));
      }
    } catch (error) {
      dispatch(phoneSearchFailed({
        query,
        error: error instanceof Error ? error.message : "An error occurred during phone search"
      }));
    }
  };
}

export async function searchContactViaPlatformPersonId(platformPersonId: string, dispatch: Dispatch): Promise<ContactV2[] | null> {
  dispatch(searchContact({ query: platformPersonId }));
  const results = await getServicesManager().getContactsViaPlatformPersonIds([platformPersonId]);
  if (results) {
    dispatch(contactSearchResultsLoaded({
      query: platformPersonId,
      items: results
    }));
  } else {
    dispatch(contactSearchFailed({
      query: platformPersonId,
      error: "No results found"
    }));
  }
  return results;
}